<template>
  <VBusHelper
    :reactOn="$constants.EVENTS.OTP.SHOW_OTP_INPUT"
    :handler="(data) => {
      otpStore.channel = data?.channel || 'email';
      otpStore.requestId = data?.requestId;
      otpStore.email = data?.email || '';
      otpStore.phoneNumber = data?.phoneNumber || '';
      otpStore.otpCode = '';
      $refs.inputModal?.show();
    }"
  />
  <VBusHelper
    :reactOn="$constants.EVENTS.OTP.SHOW_OTP_VERIFICATION"
    :handler="(data) => {
      if (data) {
        if (data.email) otpStore.email = data.email;
        if (data.phoneNumber) otpStore.phoneNumber = data.phoneNumber;
        if (data.channel) otpStore.channel = data.channel;
        if (data.requestId) otpStore.requestId = data.requestId;
      }
      otpStore.otpCode = '';
      $refs.inputModal?.hide();
      $refs.verifyModal?.show();
      startResendCountdown();
    }"
  />
  <VBusHelper
    :reactOn="$constants.EVENTS.OTP.OTP_VALIDATED"
    :handler="() => {
      $refs.verifyModal?.hide();
      $refs.inputModal?.hide();
      stopResendCountdown();
    }"
  />
  <VBusHelper
    :reactOn="$constants.EVENTS.OTP.OTP_CANCEL"
    :handler="() => {
      $refs.verifyModal?.hide();
      $refs.inputModal?.hide();
      stopResendCountdown();
    }"
  />

  <!-- Modal input email/contact -->
  <DialogComponent
    ref="inputModal"
    type="bottomsheet"
    noInnerPadding
    hideCloseButton
  >
    <div class="p-6">
      <div class="text-xl font-bold mb-2 title-color">
        {{ otpStore.channel === 'email' ? 'Réinitialisation du mot de passe' : 'Vérification du numéro' }}
      </div>
      <div class="text-sm text-slate-500 mb-6">
        {{ otpStore.channel === 'email'
          ? 'Entrez votre adresse email pour recevoir un code de vérification à 5 chiffres'
          : 'Nous vous enverrons un code de vérification par WhatsApp' }}
      </div>

      <!-- input email -->
      <div v-if="otpStore.channel === 'email'" class="mb-4">
        <label class="block text-sm font-medium text-slate-700 mb-1">Adresse email</label>
        <input
          class="inputComponent w-full"
          v-model="otpStore.email"
          type="email"
          placeholder="votre@email.com"
          :required="true"
          @keyup.enter="submitContact"
        />
      </div>

      <!-- input téléphone -->
      <template v-else>
        <div class="flex gap-2 mb-4">
          <button
            type="button"
            @click="$bus.emit($constants.EVENTS.USER?.REGISTRATION_PHONE_COUNTRY || 'user.select_registration_phone_country')"
            class="px-4 py-3 bg-slate-100 rounded-xl font-semibold text-sm min-w-[80px]"
          >
            {{ otpStore.selectedPrefix || '+229' }}
          </button>
          <input
            class="inputComponent w-full"
            v-model="otpStore.phoneNumber"
            type="tel"
            placeholder="01 01 01 01 01"
            :required="true"
            @keyup.enter="submitContact"
          />
        </div>
      </template>

      <!-- boutons -->
      <div class="flex gap-3 mt-8">
        <button
          type="button"
          @click="cancelInput"
          class="flex-1! buttonComponent py-4 bg-slate-100! border border-primary text-primary!"
        >
          annuler
        </button>
        <button
          type="button"
          @click="submitContact"
          class="flex-1! buttonComponent py-4"
          :disabled="!canSubmitContact"
        >
          continuer
        </button>
      </div>
    </div>
  </DialogComponent>

  <!-- Modal vérification OTP -->
  <DialogComponent
    ref="verifyModal"
    type="bottomsheet"
    noInnerPadding
    hideCloseButton
  >
    <div class="p-6">
      <div class="text-xl font-bold mb-2 title-color">Vérification du code</div>
      <div class="text-sm text-slate-500 mb-6">
        Entrez le code à 5 chiffres envoyé sur votre {{ otpStore.channel === 'email' ? 'email' : 'numéro' }}
        <span v-if="maskedEmail" class="font-semibold text-slate-800">
          ({{ maskedEmail }})
        </span>
        <span v-if="otpStore.phoneNumber && otpStore.channel === 'whatsapp'" class="font-semibold text-slate-800">
          (***{{ otpStore.phoneNumber.slice(-4) }})
        </span>
      </div>

      <!-- OTP input -->
      <div class="mb-6 flex justify-center">
        <OtpInput class="h-16 w-full max-w-sm" v-model="otpStore.otpCode" :nb-char="5" />
      </div>

      <!-- boutons -->
      <div class="flex gap-3 mb-4">
        <button
          type="button"
          @click="cancelVerify"
          class="flex-1! buttonComponent py-4 bg-slate-100! border border-primary text-primary!"
        >
          annuler
        </button>
        <button
          type="button"
          @click="verifyCode"
          class="flex-1! buttonComponent py-4"
          :disabled="!otpStore.otpCode || otpStore.otpCode.length !== 5"
        >
          vérifier
        </button>
      </div>

      <!-- renvoyer -->
      <div class="text-center mt-2">
        <button
          type="button"
          v-if="resendCountdown <= 0"
          @click="resendCode"
          class="text-sm text-primary font-semibold underline cursor-pointer"
        >
          renvoyer le code
        </button>
        <span v-else class="text-xs text-slate-400">
          Renvoyer le code dans {{ resendCountdown }}s
        </span>
      </div>
    </div>
  </DialogComponent>
</template>

<script>
import { reactive, computed, ref } from 'vue';

export default {
  name: 'OtpModal',

  setup() {
    const otpStore = reactive({
      channel: 'email',
      email: '',
      phoneNumber: '',
      prefix: '229',
      selectedPrefix: '+229',
      otpCode: '',
      requestId: null
    });

    const resendCountdown = ref(0);
    let timer = null;

    const startResendCountdown = (seconds = 60) => {
      resendCountdown.value = seconds;
      clearInterval(timer);
      timer = setInterval(() => {
        if (resendCountdown.value > 0) {
          resendCountdown.value--;
        } else {
          clearInterval(timer);
        }
      }, 1000);
    };

    const stopResendCountdown = () => {
      clearInterval(timer);
      resendCountdown.value = 0;
    };

    const maskedEmail = computed(() => {
      if (otpStore.channel === 'email' && otpStore.email) {
        const parts = otpStore.email.split('@');
        if (parts.length === 2) {
          const [local, domain] = parts;
          if (local.length > 2) {
            return `${local[0]}${local[1]}***@${domain}`;
          }
          return `${local[0]}***@${domain}`;
        }
        return otpStore.email;
      }
      return '';
    });

    const canSubmitContact = computed(() => {
      if (otpStore.channel === 'email') {
        return !!otpStore.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(otpStore.email.trim());
      }
      return !!otpStore.phoneNumber && otpStore.phoneNumber.replace(/\D/g, '').length >= 8;
    });

    return {
      otpStore,
      maskedEmail,
      canSubmitContact,
      resendCountdown,
      startResendCountdown,
      stopResendCountdown
    };
  },

  methods: {
    submitContact() {
      if (!this.canSubmitContact) return;
      this.$bus.emit(this.$constants.EVENTS.OTP.OTP_SUBMIT_CONTACT, {
        email: this.otpStore.email,
        phoneNumber: this.otpStore.phoneNumber,
        prefix: this.otpStore.prefix
      });
    },

    verifyCode() {
      if (!this.otpStore.otpCode || this.otpStore.otpCode.length !== 5) return;
      this.$bus.emit(this.$constants.EVENTS.OTP.OTP_VERIFY, this.otpStore.otpCode);
    },

    resendCode() {
      if (this.resendCountdown > 0) return;
      this.startResendCountdown(60);
      this.$bus.emit(this.$constants.EVENTS.OTP.OTP_RESEND);
    },

    cancelInput() {
      this.$refs.inputModal?.hide();
      this.$bus.emit(this.$constants.EVENTS.OTP.OTP_CANCEL);
    },

    cancelVerify() {
      this.$refs.verifyModal?.hide();
      this.$bus.emit(this.$constants.EVENTS.OTP.OTP_CANCEL);
    }
  }
};
</script>
